import React from 'react';
import { ArticlePerformanceRow, SortBy, SortOrder } from '../../../../services/performanceMetricsService';
import {
  decodeHtmlEntities,
  formatDate,
  formatNumber,
  formatPercent,
  formatPosition,
  positionDelta,
  positionTone,
} from './performanceFormatters';

// ---------------------------------------------------------------------------
// Row — one article in the performance table
//
// Columns: Title | Category | Published | Indexed | Position | Impr | Clicks | CTR | Sessions | Bounce | Conv
// ---------------------------------------------------------------------------

export const PerformanceRow: React.FC<{ row: ArticlePerformanceRow; onClick: () => void }> = ({ row, onClick }) => {
  const title = decodeHtmlEntities(row.title);
  const posDelta = positionDelta(row.latest_position, row.avg_position);
  const hasGsc = row.matched_to_gsc;
  const hasGa4 = (row.ga4_sessions ?? 0) > 0;

  // Channel shown as tooltip on sessions
  const channelTooltip = hasGa4
    ? `${row.ga4_pageviews ?? 0} pageviews · ${row.ga4_sessions ?? 0} sessions · Top: ${row.ga4_top_channel || 'unknown'} (${row.ga4_data_points ?? 0} days)`
    : 'No GA4 data for this period';

  return (
    <div className="flex items-center gap-2 px-3 py-3 hover:bg-gray-50 cursor-pointer" onClick={onClick}>
      {/* Title + badge */}
      <div className="flex-1 min-w-0 pr-2">
        <div className="flex items-center gap-2">
          <a
            href={row.url}
            target="_blank"
            rel="noopener noreferrer"
            className="font-medium text-gray-900 hover:text-blue-600 truncate block text-sm"
            title={title}
            onClick={(e) => e.stopPropagation()}
          >
            {title}
          </a>
          {row.from_pipeline && (
            <span
              title="Generated by the Talaera content pipeline"
              className="inline-flex items-center justify-center px-1 rounded bg-blue-50 text-blue-600 text-[9px] font-medium uppercase shrink-0"
            >
              T
            </span>
          )}
        </div>
      </div>

      {/* Category */}
      <div className="w-24 shrink-0 text-xs text-gray-500 truncate" title={row.category || ''}>
        {row.category || <span className="text-gray-300">&mdash;</span>}
      </div>

      {/* Pillar */}
      <div className="w-20 shrink-0 text-xs text-gray-500 truncate" title={row.pillar || ''}>
        {row.pillar || <span className="text-gray-300">&mdash;</span>}
      </div>

      {/* Funnel */}
      <div className="w-16 shrink-0 text-xs text-gray-500 truncate" title={row.funnel_stage || ''}>
        {row.funnel_stage || <span className="text-gray-300">&mdash;</span>}
      </div>

      {/* Published */}
      <div className="w-20 shrink-0 text-xs text-gray-500">
        {formatDate(row.published_at)}
      </div>

      {/* Updated */}
      <div className="w-20 shrink-0 text-xs text-gray-500">
        {row.modified_at && row.modified_at !== row.published_at ? formatDate(row.modified_at) : '\u2014'}
      </div>

      {/* Indexed */}
      <div className="w-10 shrink-0 text-center">
        <IndexationIcon row={row} />
      </div>

      {/* Position (avg + trend) */}
      <div className="w-16 shrink-0 text-right" title={hasGsc && row.avg_position != null ? `28d avg: ${formatPosition(row.avg_position)}, latest: ${formatPosition(row.latest_position)}` : 'No GSC data'}>
        {hasGsc ? (
          <div className="flex items-center justify-end gap-1">
            <span className={`text-sm tabular-nums font-medium ${positionTone(row.avg_position)}`}>
              {formatPosition(row.avg_position)}
            </span>
            {posDelta && <span className={`text-xs leading-none ${posDelta.tone}`}>{posDelta.symbol}</span>}
          </div>
        ) : (
          <span className="text-gray-300 text-sm">&mdash;</span>
        )}
      </div>

      {/* Impressions */}
      <div className="w-16 shrink-0 text-right">
        {hasGsc ? (
          <span className="text-sm text-gray-700 tabular-nums" title={`${row.impressions.toLocaleString()} impressions (28d)`}>
            {formatNumber(row.impressions)}
          </span>
        ) : (
          <span className="text-gray-300 text-sm">&mdash;</span>
        )}
      </div>

      {/* Clicks */}
      <div className="w-16 shrink-0 text-right">
        {hasGsc ? (
          <span className="text-sm font-semibold text-gray-900 tabular-nums" title={`${row.clicks.toLocaleString()} clicks (28d)`}>
            {formatNumber(row.clicks)}
          </span>
        ) : (
          <span className="text-gray-300 text-sm">&mdash;</span>
        )}
      </div>

      {/* CTR */}
      <div className="w-14 shrink-0 text-right">
        {hasGsc ? (
          <span className="text-sm text-gray-700 tabular-nums">{formatPercent(row.ctr)}</span>
        ) : (
          <span className="text-gray-300 text-sm">&mdash;</span>
        )}
      </div>

      {/* GA4 Sessions (channel in tooltip) */}
      <div className="w-16 shrink-0 text-right" title={channelTooltip}>
        {hasGa4 ? (
          <span className="text-sm text-gray-700 tabular-nums">{formatNumber(row.ga4_sessions ?? 0)}</span>
        ) : (
          <span className="text-gray-300 text-sm">&mdash;</span>
        )}
      </div>

      {/* Bounce Rate */}
      <div className="w-14 shrink-0 text-right">
        {hasGa4 && row.ga4_bounce_rate != null ? (
          <span className={`text-sm tabular-nums ${row.ga4_bounce_rate > 0.6 ? 'text-amber-600' : 'text-gray-700'}`}>
            {(row.ga4_bounce_rate * 100).toFixed(0)}%
          </span>
        ) : (
          <span className="text-gray-300 text-sm">&mdash;</span>
        )}
      </div>

      {/* Conversions */}
      <div className="w-12 shrink-0 text-right">
        {(row.ga4_conversions ?? 0) > 0 ? (
          <span className="text-sm font-semibold text-green-700 tabular-nums">{row.ga4_conversions}</span>
        ) : hasGa4 ? (
          <span className="text-gray-300 text-sm">0</span>
        ) : (
          <span className="text-gray-300 text-sm">&mdash;</span>
        )}
      </div>

      {/* AI Sessions */}
      <div className="w-10 shrink-0 text-right">
        {((row as any).ga4_ai_sessions ?? 0) > 0 ? (
          <span className="text-sm font-semibold text-emerald-600 tabular-nums">{(row as any).ga4_ai_sessions}</span>
        ) : hasGa4 ? (
          <span className="text-gray-300 text-sm">0</span>
        ) : (
          <span className="text-gray-300 text-sm">&mdash;</span>
        )}
      </div>
    </div>
  );
};

// ---------------------------------------------------------------------------
// IndexationIcon
// ---------------------------------------------------------------------------

const IndexationIcon: React.FC<{ row: ArticlePerformanceRow }> = ({ row }) => {
  if (row.is_indexed === true) {
    return (
      <span title={`Indexed: ${row.coverage_state || 'on Google'}`} className="text-green-600 text-sm font-bold leading-none">
        ✓
      </span>
    );
  }
  if (row.is_indexed === false) {
    return (
      <span title={`Not indexed: ${row.coverage_state || 'no'}`} className="text-red-500 text-sm font-bold leading-none">
        ✕
      </span>
    );
  }
  return <span title="Not yet checked" className="text-gray-300 text-sm">&mdash;</span>;
};

// ---------------------------------------------------------------------------
// SortHeader
// ---------------------------------------------------------------------------

export const SortHeader: React.FC<{
  label: string;
  col: SortBy;
  current: SortBy;
  order: SortOrder;
  onClick: (col: SortBy) => void;
  disabled?: boolean;
  className?: string;
  tooltip?: string;
}> = ({ label, col, current, order, onClick, disabled, className, tooltip }) => {
  const isActive = current === col;
  const arrow = isActive ? (order === 'desc' ? '↓' : '↑') : '';
  return (
    <button
      onClick={() => onClick(col)}
      disabled={disabled}
      title={tooltip}
      className={`text-left hover:text-gray-700 disabled:cursor-not-allowed ${isActive ? 'text-gray-900' : ''} ${className || ''}`}
    >
      {label}
      {arrow && <span className="ml-1">{arrow}</span>}
    </button>
  );
};
